<template>
  <Teleport
    v-if="isModalActive"
    :to="modalRoot"
  >
    <div
      :id="$props.id"
      ref="modalContainerRef"
      :inert="!isModalOpen"
      class="dvp-modal-container"
      role="dialog"
      :aria-labelledby="titleId"
    >
      <template v-if="isModalOpen">
        <div
          :data-a11y-dialog-hide="$props.isAlert ? undefined : true"
          class="dvp-modal-overlay"
        />

        <transition
          mode="out-in"
          :name="$props.animationName"
        >
          <slot />
        </transition>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
  import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

  import A11yDialog from 'a11y-dialog'

  import events$ from '@shared/services/Events'

  import { EVENT_MODAL } from '@shared/config/events'

  // INIT
  const animationDuration = {
    timeout: 300,
    css: '300ms',
  }
  const emit = defineEmits(['click-primary', 'click-secondary', 'click-tertiary', 'close'])
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },

    isAlert: {
      type: Boolean,
      default: false,
    },

    modalRoot: {
      type: String,
      default: 'body',
    },

    animationName: {
      type: String,
      default: null,
    },
  })

  // DATA
  const isModalActive = ref(false)
  const isModalOpen = ref(false)
  const isInitialOpen = ref(true)

  const a11yDialog = ref()
  const modalContainerRef = ref()
  const fromHeight = ref(0)

  // COMPUTED
  const titleId = computed(() => {
    return `${props.id}-title`
  })

  // METHODS
  function close() {
    a11yDialog.value.hide()

    modalContainerRef.value.removeAttribute('aria-hidden')
  }

  function disableScrolling() {
    const x = window.scrollX
    const y = window.scrollY

    window.onscroll = function () {
      window.scrollTo(x, y)
    }
  }

  async function open() {
    isModalActive.value = true

    await nextTick()

    a11yDialog.value = new A11yDialog(modalContainerRef.value)

    a11yDialog.value.on('show', onModalShow)
    a11yDialog.value.on('hide', onModalHide)

    a11yDialog.value.show()
  }

  function doIt(read = false) {
    const el = modalContainerRef.value?.querySelector('.dvp-modal')
    if (!el) return
    const _h = getComputedStyle(el).height

    if (read) {
      return fromHeight.value || _h
    } else {
      fromHeight.value = _h
    }
  }

  function onModalShow() {
    disableScrolling()

    events$.emit(EVENT_MODAL.OPEN, { modalName: props.id })

    isModalOpen.value = true
  }

  async function onModalHide() {
    // Re-Enable background scrolling
    window.onscroll = null

    // Inform listeners (e.g. dropdown) to cleanup before modal is closed
    events$.emit(EVENT_MODAL.BEFORE_CLOSING, props.id)

    // Set closing css class
    modalContainerRef.value?.classList.add('dvp-modal-container--closing')

    // Await closing after event, to give listeners time to cleanup and await closing animation
    await new Promise(s => setTimeout(s, animationDuration.timeout))

    modalContainerRef.value?.classList.remove('dvp-modal-container--closing')

    // Inform listeners (e.g. tracking) that modal is now closed
    events$.emit(EVENT_MODAL.CLOSE, { modalName: props.id })

    emit('close')

    isModalOpen.value = false

    await nextTick()

    isInitialOpen.value = true
    isModalActive.value = false
  }

  // LIFECYCLE HOOKS
  onBeforeUnmount(() => {
    // Re-Enable background scrolling, e.g. when user navigates back via browser-back
    window.onscroll = null

    isModalOpen.value = false
    isInitialOpen.value = true
    isModalActive.value = false
  })

  // EPILOGUE
  defineExpose({
    id: props.id,
    open,
    close,

    isModalActive,
    isModalOpen,
    isInitialOpen,
    doIt,
  })
</script>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateX(0);
  }
}

.dvp-modal-container,
.dvp-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.dvp-modal-container {
  z-index: var(--dvp-stack-level-modal);
}

.dvp-modal-overlay {
  background-color: var(--scrim);
  backdrop-filter: blur(2px);
}

.dvp-modal-container--closing .dvp-modal-overlay,
.dvp-modal-container--closing .dvp-modal-wrapper {
  animation-name: fade-out;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
}

.dvp-modal-container:not(.dvp-modal-container--closing)[inert='true'] {
  display: none;
}

.dvp-modal-overlay {
  animation-name: fade-in;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
}

.dvp-modal-wrapper:not(.animation-step) {
  animation-name: scale-in;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);

  &.dvp-modal-wrapper--maxi {
    animation-name: slide-up;
  }
}

.dvp-modal-container :deep(.dvp-modal) {
  transition: height 1s ease;
}
</style>
